import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const COMMON_HOLIDAYS = [
  { name: "Mother's Day", month: null, day: null },
  { name: "Father's Day", month: null, day: null },
  { name: "Christmas", month: 12, day: 25 },
  { name: "Valentine's Day", month: 2, day: 14 },
  { name: "Thanksgiving", month: null, day: null },
  { name: "Hanukkah", month: 12, day: 7 },
  { name: "Lunar New Year", month: 1, day: 29 },
  { name: "Easter", month: null, day: null },
  { name: "Diwali", month: 10, day: 20 },
];

function EventBadge({ event }) {
  const colors = {
    birthday: { bg: '#EDE7F6', text: '#5E35B1' },
    anniversary: { bg: '#FFF3E0', text: '#E65100' },
    holiday: { bg: '#E8F5E9', text: '#2E7D32' }
  };
  const c = colors[event.event_type] || colors.birthday;
  return (
    <div style={{
      background: c.bg, color: c.text,
      fontSize: '0.7rem', padding: '1px 4px', borderRadius: '3px',
      marginBottom: '1px', overflow: 'hidden', textOverflow: 'ellipsis',
      whiteSpace: 'nowrap', lineHeight: '1.4'
    }}>
      {event.name}
    </div>
  );
}

function CalendarView({ events }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const eventsByDate = {};
  events.forEach(e => {
    if (!e.date || typeof e.date !== 'string') return;
    const d = e.date;
    if (!eventsByDate[d]) eventsByDate[d] = [];
    eventsByDate[d].push(e);
  });

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <button onClick={prevMonth} style={{ background: '#999', padding: '0.3rem 0.8rem', fontSize: '0.85rem' }}>&larr;</button>
        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{MONTH_NAMES[month]} {year}</h3>
        <button onClick={nextMonth} style={{ background: '#999', padding: '0.3rem 0.8rem', fontSize: '0.85rem' }}>&rarr;</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', background: '#ddd', border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden' }}>
        {DAY_NAMES.map(d => (
          <div key={d} style={{ background: '#f5f0eb', padding: '0.4rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#5D4037' }}>{d}</div>
        ))}
        {cells.map((day, i) => {
          const dateStr = day ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
          const dayEvents = dateStr ? (eventsByDate[dateStr] || []) : [];
          const isToday = dateStr === todayStr;
          return (
            <div key={i} style={{
              background: isToday ? '#FFF8E1' : '#fff',
              minHeight: '70px', padding: '0.25rem',
              opacity: day ? 1 : 0.3
            }}>
              {day && (
                <>
                  <div style={{ fontSize: '0.8rem', fontWeight: isToday ? '700' : '400', color: isToday ? '#5D4037' : '#666', marginBottom: '2px' }}>{day}</div>
                  {dayEvents.slice(0, 3).map((e, j) => <EventBadge key={j} event={e} />)}
                  {dayEvents.length > 3 && <div style={{ fontSize: '0.65rem', color: '#888' }}>+{dayEvents.length - 3} more</div>}
                </>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', fontSize: '0.8rem', color: '#888' }}>
        <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#EDE7F6', borderRadius: '2px', marginRight: '4px' }}></span>Birthday</span>
        <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#FFF3E0', borderRadius: '2px', marginRight: '4px' }}></span>Anniversary</span>
        <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#E8F5E9', borderRadius: '2px', marginRight: '4px' }}></span>Holiday</span>
      </div>
    </div>
  );
}

function formatEventDate(dateStr) {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length < 3) return dateStr;
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    if (!months[m]) return dateStr;
    return `${months[m]} ${d}`;
  } catch { return dateStr; }
}

function ListView({ events }) {
  return (
    <div>
      {events.length === 0 && <p style={{ color: '#888' }}>No upcoming events found.</p>}
      {events.map((e, i) => {
        const days = typeof e.days_until === 'number' && !isNaN(e.days_until) ? Math.max(0, e.days_until) : null;
        const typeLabel = e.event_type === 'birthday' ? 'Birthday' : e.event_type === 'anniversary' ? 'Anniversary' : 'Holiday';
        return (
          <div className="event-row" key={i}>
            <div>
              <strong>{e.name}</strong>
              <span style={{ color: '#888', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                {e.relationship ? `${e.relationship} · ` : ''}{typeLabel}{e.date ? ` · ${formatEventDate(e.date)}` : ''}
              </span>
            </div>
            <div className={`days ${days !== null && days <= 14 ? 'soon' : ''}`}>
              {days === null ? '—' : days === 0 ? 'Today!' : `${days}d`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AddHolidayPanel({ userId, existingHolidays, onAdded, onClose }) {
  const [selectedName, setSelectedName] = useState('');
  const [customName, setCustomName] = useState('');
  const [customMonth, setCustomMonth] = useState('');
  const [customDay, setCustomDay] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const existingNames = existingHolidays.map(h => h.name);
  const available = COMMON_HOLIDAYS.filter(h => !existingNames.includes(h.name));

  const save = async () => {
    const name = selectedName === '__custom__' ? customName.trim() : selectedName;
    if (!name) { setMsg('Please select or enter a holiday name.'); return; }
    if (existingHolidays.length >= 7) { setMsg('You\'ve reached the limit of 7 holidays.'); return; }

    let month = null, day = null;
    if (selectedName === '__custom__') {
      month = customMonth ? parseInt(customMonth, 10) : null;
      day = customDay ? parseInt(customDay, 10) : null;
    } else {
      const preset = COMMON_HOLIDAYS.find(h => h.name === selectedName);
      if (preset) { month = preset.month; day = preset.day; }
    }

    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch('/api/holidays', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, name, month, day })
      });
      if (res.status === 400) {
        const d = await res.json();
        setMsg(d.error || 'Could not add holiday.');
        setSaving(false);
        return;
      }
      onAdded();
      onClose();
    } catch { setMsg('Something went wrong.'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', padding: '1.5rem 1.25rem 2rem', width: '100%', maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>Add a holiday</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#888' }}>✕</button>
        </div>

        <label style={{ fontSize: '0.85rem', color: '#888', display: 'block', marginBottom: '0.4rem' }}>Choose a holiday</label>
        <select
          value={selectedName}
          onChange={e => setSelectedName(e.target.value)}
          style={{ width: '100%', marginBottom: '1rem', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem' }}
        >
          <option value="">— Pick from list —</option>
          {available.map((h, i) => <option key={i} value={h.name}>{h.name}</option>)}
          <option value="__custom__">+ Custom holiday...</option>
        </select>

        {selectedName === '__custom__' && (
          <div style={{ marginBottom: '1rem' }}>
            <input
              value={customName}
              onChange={e => setCustomName(e.target.value)}
              placeholder="Holiday name"
              style={{ width: '100%', marginBottom: '0.5rem' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select value={customMonth} onChange={e => setCustomMonth(e.target.value)} style={{ flex: 2 }}>
                <option value="">Month (optional)</option>
                {MONTH_NAMES.map((m, i) => <option key={i} value={String(i + 1)}>{m}</option>)}
              </select>
              <input
                value={customDay}
                onChange={e => setCustomDay(e.target.value.replace(/\D/g, '').slice(0, 2))}
                placeholder="Day"
                inputMode="numeric"
                style={{ flex: 1 }}
              />
            </div>
          </div>
        )}

        {msg && <div style={{ color: '#c62828', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{msg}</div>}

        <button
          onClick={save}
          disabled={saving || !selectedName}
          style={{ width: '100%', background: '#5D4037', opacity: selectedName ? 1 : 0.5 }}
        >
          {saving ? 'Saving...' : 'Add Holiday'}
        </button>
      </div>
    </div>
  );
}

const MOTHERS_DAY_ITEMS = [
  { key: 'card', label: 'Send a card', icon: '✉️' },
  { key: 'flowers', label: 'Order flowers', icon: '🌸' },
  { key: 'reservation', label: 'Make a reservation (or send a gift card)', icon: '🍽️' },
  { key: 'gift', label: 'Gift ordered', icon: '🎁' },
];

function getMothersDay2026() {
  return new Date(2026, 4, 10);
}

function isMathersDayActive() {
  const now = new Date();
  const md = getMothersDay2026();
  const start = new Date(2026, 3, 10);
  const end = new Date(2026, 4, 11);
  return now >= start && now <= end;
}

function MothersDayChecklist({ userId, momContact, giftIdeas }) {
  const [checklist, setChecklist] = useState({});
  const [saving, setSaving] = useState(false);
  const [showGiftPrompt, setShowGiftPrompt] = useState(false);
  const [giftText, setGiftText] = useState('');
  const [giftSaved, setGiftSaved] = useState(false);
  const year = 2026;

  useEffect(() => {
    fetch(`/api/mothers-day-checklist/${userId}?year=${year}`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          const map = {};
          data.forEach(row => { map[row.item_key] = !!row.checked; });
          setChecklist(map);
        }
      })
      .catch(() => {});
  }, [userId]);

  const toggle = async (key) => {
    const newVal = !checklist[key];
    setChecklist(prev => ({ ...prev, [key]: newVal }));
    setSaving(true);
    try {
      await fetch('/api/mothers-day-checklist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, item_key: key, checked: newVal, year }),
      });
    } catch {}
    setSaving(false);
    if (key === 'gift' && newVal) setShowGiftPrompt(true);
  };

  const saveGiftIdea = async () => {
    if (!momContact || !giftText.trim()) return;
    try {
      await fetch('/api/contacts/' + momContact.id + '/gifts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idea: giftText.trim() }),
      });
      setGiftSaved(true);
      setShowGiftPrompt(false);
    } catch {}
  };

  const md = getMothersDay2026();
  const now = new Date();
  const msDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msDayTarget = new Date(md.getFullYear(), md.getMonth(), md.getDate());
  const daysUntil = Math.ceil((msDayTarget - msDay) / (1000 * 60 * 60 * 24));

  return (
    <div style={{
      background: '#fff8f5',
      border: '1.5px solid #e8c9b8',
      borderRadius: '12px',
      padding: '1.25rem',
      marginBottom: '2rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{ fontSize: '1.5rem' }}>🌷</span>
          <div>
            <div style={{ fontWeight: '600', fontSize: '1rem', color: '#3d2b1f' }}>
              Mother's Day Checklist
            </div>
            <div style={{ fontSize: '0.78rem', color: '#9a7c6e' }}>
              May 10, 2026{daysUntil > 0 ? ` · ${daysUntil} day${daysUntil !== 1 ? 's' : ''} away` : daysUntil === 0 ? ' · Today!' : ''}
            </div>
          </div>
        </div>
        <Link to="/mothers-day" style={{ fontSize: '0.75rem', color: '#9a7c6e', textDecoration: 'none', whiteSpace: 'nowrap' }}>
          Tips & ideas →
        </Link>
      </div>

      {momContact && (
        <div style={{ background: '#f5e9e0', borderRadius: '6px', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#5a3e33' }}>
          For <strong>{momContact.first_name}{momContact.last_name ? ' ' + momContact.last_name : ''}</strong>
          {giftIdeas && giftIdeas.length > 0 && (
            <span style={{ color: '#888', marginLeft: '0.4rem' }}>
              · Saved idea: {giftIdeas[0].idea}
            </span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {MOTHERS_DAY_ITEMS.map(item => (
          <label key={item.key} style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            cursor: 'pointer', padding: '0.5rem 0.6rem',
            borderRadius: '8px',
            background: checklist[item.key] ? '#f0e8e0' : 'transparent',
            transition: 'background 0.15s',
          }}>
            <input
              type="checkbox"
              checked={!!checklist[item.key]}
              onChange={() => toggle(item.key)}
              style={{ width: '16px', height: '16px', accentColor: '#5D4037', cursor: 'pointer', flexShrink: 0 }}
            />
            <span style={{ fontSize: '1rem', flexShrink: 0 }}>{item.icon}</span>
            <span style={{
              fontSize: '0.9rem',
              color: checklist[item.key] ? '#9a7c6e' : '#3d2b1f',
              textDecoration: checklist[item.key] ? 'line-through' : 'none',
            }}>
              {item.label}
            </span>
          </label>
        ))}
      </div>

      {showGiftPrompt && momContact && !giftSaved && (
        <div style={{ marginTop: '0.75rem', background: '#fff', border: '1px solid #e8c9b8', borderRadius: '8px', padding: '0.75rem' }}>
          <div style={{ fontSize: '0.85rem', color: '#3d2b1f', marginBottom: '0.5rem' }}>
            Save what you got as a gift idea for next year?
          </div>
          <input
            value={giftText}
            onChange={e => setGiftText(e.target.value)}
            placeholder="e.g. Spa gift card from Burke Williams"
            style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', marginBottom: '0.5rem' }}
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={saveGiftIdea} disabled={!giftText.trim()} style={{ flex: 1, background: '#5D4037', fontSize: '0.82rem', padding: '0.4rem' }}>
              Save idea
            </button>
            <button onClick={() => setShowGiftPrompt(false)} style={{ flex: 1, background: '#ccc', fontSize: '0.82rem', padding: '0.4rem' }}>
              Skip
            </button>
          </div>
        </div>
      )}
      {giftSaved && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: '#6B7F5E' }}>Gift idea saved to her profile.</div>
      )}
    </div>
  );
}

export default function Upcoming({ userId }) {
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [view, setView] = useState('list');
  const [showAddHoliday, setShowAddHoliday] = useState(false);
  const [momContact, setMomContact] = useState(null);
  const [momGiftIdeas, setMomGiftIdeas] = useState([]);

  const reload = useCallback(() => {
    if (userId) {
      fetch(`/api/contacts/user/${userId}/upcoming`).then(r => r.json()).then(data => {
        setEvents(Array.isArray(data) ? data : []);
      }).catch(() => setEvents([]));
      fetch(`/api/holidays/user/${userId}`).then(r => r.json()).then(data => {
        setHolidays(Array.isArray(data) ? data : []);
      }).catch(() => setHolidays([]));
      fetch(`/api/contacts/user/${userId}`).then(r => r.json()).then(data => {
        if (Array.isArray(data)) {
          const mom = data.find(c => c.relationship === 'Mom' || c.relationship === 'Mother');
          setMomContact(mom || null);
          setMomGiftIdeas(mom && Array.isArray(mom.gift_ideas) ? mom.gift_ideas : []);
        }
      }).catch(() => {});
    }
  }, [userId]);

  useEffect(() => { reload(); }, [userId, reload]);

  const removeHoliday = async (id) => {
    try {
      await fetch(`/api/holidays/${id}`, { method: 'DELETE' });
      reload();
    } catch {}
  };

  if (!userId) return <p>Please sign up first.</p>;

  return (
    <div>
      {showAddHoliday && (
        <AddHolidayPanel
          userId={userId}
          existingHolidays={holidays}
          onAdded={reload}
          onClose={() => setShowAddHoliday(false)}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Upcoming Events</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            onClick={() => setShowAddHoliday(true)}
            style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem', background: '#6B7F5E', borderRadius: '6px' }}
          >
            + Holiday
          </button>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button
              onClick={() => setView('list')}
              style={{
                fontSize: '0.8rem', padding: '0.35rem 0.75rem',
                background: view === 'list' ? '#2c2c2c' : '#ccc',
                borderRadius: '4px 0 0 4px'
              }}
            >List</button>
            <button
              onClick={() => setView('calendar')}
              style={{
                fontSize: '0.8rem', padding: '0.35rem 0.75rem',
                background: view === 'calendar' ? '#2c2c2c' : '#ccc',
                borderRadius: '0 4px 4px 0'
              }}
            >Calendar</button>
          </div>
        </div>
      </div>

      {isMathersDayActive() && (
        <MothersDayChecklist
          userId={userId}
          momContact={momContact}
          giftIdeas={momGiftIdeas}
        />
      )}

      {view === 'list' ? <ListView events={events.filter(e => e && typeof e.days_until === 'number' && !isNaN(e.days_until))} /> : <CalendarView events={events.filter(e => e && e.date && typeof e.date === 'string')} />}

      {holidays.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ fontSize: '1rem', color: '#5D4037', marginBottom: '0.75rem' }}>My Holidays</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {holidays.map(h => (
              <div key={h.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#E8F5E9', color: '#2E7D32', borderRadius: '20px', padding: '0.35rem 0.8rem', fontSize: '0.85rem' }}>
                <span>{h.name}{h.month && h.day ? ` (${h.month}/${h.day})` : ''}</span>
                <button
                  onClick={() => removeHoliday(h.id)}
                  style={{ background: 'transparent', border: 'none', color: '#999', cursor: 'pointer', fontSize: '0.85rem', padding: 0, lineHeight: 1, marginLeft: '2px' }}
                  title="Remove holiday"
                >✕</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
